if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.cursorNav = this.querySelector('[data-media-cursor-nav]');

        this.initCursorNavigation();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener(
              'click',
              this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
            );
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      initCursorNavigation() {
        if (!this.cursorNav || !this.elements.viewer) return;

        this.elements.viewer.addEventListener(
          'mousemove',
          this.onCursorNavigationMove.bind(this)
        );

        this.elements.viewer.addEventListener(
          'mouseleave',
          this.hideCursorNavigation.bind(this)
        );

        this.cursorNav.addEventListener(
          'click',
          this.onCursorNavigationClick.bind(this)
        );

        this.mql.addEventListener('change', (event) => {
          if (!event.matches) {
            this.hideCursorNavigation();
          }
        });
      }

      onCursorNavigationMove(event) {
        if (!this.mql.matches || !this.cursorNav) return;

        const activeMedia =
          this.elements.viewer.querySelector('[data-media-id].is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (
          !activeMedia ||
          !activeMedia.querySelector('.product__modal-opener--image')
        ) {
          this.hideCursorNavigation();
          return;
        }

        const mediaRect = activeMedia.getBoundingClientRect();

        const isInsideMedia =
          event.clientX >= mediaRect.left &&
          event.clientX <= mediaRect.right &&
          event.clientY >= mediaRect.top &&
          event.clientY <= mediaRect.bottom;

        if (!isInsideMedia) {
          this.hideCursorNavigation();
          return;
        }

        const viewerRect = this.elements.viewer.getBoundingClientRect();

        const direction =
          event.clientX < mediaRect.left + mediaRect.width / 2
            ? 'prev'
            : 'next';

        this.cursorNav.dataset.direction = direction;

        this.cursorNav.classList.toggle(
          'is-prev',
          direction === 'prev'
        );

        this.cursorNav.classList.toggle(
          'is-next',
          direction === 'next'
        );

        this.cursorNav.classList.add('is-visible');

        this.elements.viewer.classList.add(
          'is-cursor-navigation'
        );

        this.cursorNav.style.left = `${
          event.clientX - viewerRect.left
        }px`;

        this.cursorNav.style.top = `${
          event.clientY - viewerRect.top
        }px`;

        this.cursorNav.setAttribute(
          'aria-label',
          direction === 'prev'
            ? this.cursorNav.dataset.prevLabel
            : this.cursorNav.dataset.nextLabel
        );

        /*
         * 这里只获取真正的大图 slide。
         *
         * product-thumbnail 内部同样存在 data-media-id，
         * 所以不能直接使用：
         *
         * querySelectorAll('[data-media-id]')
         *
         * 否则下一项可能取到大图内部的 button / deferred-media。
         */
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(
            '.product__media-item[data-media-id]'
          )
        );

        const activeIndex = mediaItems.indexOf(activeMedia);

        const isDisabled =
          (direction === 'prev' && activeIndex <= 0) ||
          (
            direction === 'next' &&
            activeIndex >= mediaItems.length - 1
          );

        this.cursorNav.setAttribute(
          'aria-disabled',
          isDisabled ? 'true' : 'false'
        );
      }

      onCursorNavigationClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (
          !this.mql.matches ||
          !this.cursorNav ||
          this.cursorNav.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector('[data-media-id].is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        /*
         * 同样只获取真正的大图 slide。
         */
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(
            '.product__media-item[data-media-id]'
          )
        );

        const activeIndex = mediaItems.indexOf(activeMedia);

        if (activeIndex === -1) return;

        const nextIndex =
          this.cursorNav.dataset.direction === 'prev'
            ? activeIndex - 1
            : activeIndex + 1;

        const nextMedia = mediaItems[nextIndex];

        if (!nextMedia) return;

        /*
         * 继续复用主题原来的 setActiveMedia。
         * 不重新实现切图、缩略图 active、缩略图滚动等逻辑。
         */
        this.setActiveMedia(
          nextMedia.dataset.mediaId,
          false
        );
      }

      hideCursorNavigation() {
        if (!this.cursorNav || !this.elements.viewer) return;

        this.cursorNav.classList.remove(
          'is-visible',
          'is-prev',
          'is-next'
        );

        this.elements.viewer.classList.remove(
          'is-cursor-navigation'
        );
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) {
          return;
        }

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute('aria-current')
          );

        thumbnail
          .querySelector('button')
          .setAttribute('aria-current', true);

        // if (
        //   this.elements.thumbnails.isSlideVisible(
        //     thumbnail,
        //     10
        //   )
        // ) {
        //   return;
        // }

        // this.elements.thumbnails.slider.scrollTo({
        //   left: thumbnail.offsetLeft,
        // });
        const slider = this.elements.thumbnails.slider;
        const offset = thumbnail.clientWidth;
        
        const isNearLeft =
          thumbnail.offsetLeft < slider.scrollLeft + offset;
        
        const isNearRight =
          thumbnail.offsetLeft + thumbnail.clientWidth >
          slider.scrollLeft + slider.clientWidth - offset;
        
        if (!isNearLeft && !isNearRight) return;
        
        if (isNearLeft) {
          slider.scrollTo({
            left: Math.max(0, thumbnail.offsetLeft - offset),
            behavior: 'smooth',
          });
        } else {
          slider.scrollTo({
            left:
              thumbnail.offsetLeft +
              thumbnail.clientWidth -
              slider.clientWidth +
              offset,
            behavior: 'smooth',
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}